import {
  FormControl,
  FormHelperText,
  InputAdornment,
  OutlinedInput,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { FormikValues, useFormik } from 'formik';
import { Fragment, type ReactNode } from 'react';
import { useUniqueId } from '~/hooks/unique-id';

export interface NumberPairItem<T extends FormikValues> {
  label: string;
  name: keyof T & string;
  sx?: SxProps<Theme>;
}

export interface NumberPairFieldProps<T extends FormikValues> {
  fields: readonly [NumberPairItem<T>, NumberPairItem<T>];
  formik: ReturnType<typeof useFormik<T>>;
  /** Names the pair as a whole; each input is named by its own `label`. */
  label: string;
  required?: boolean;
  separator?: ReactNode;
  unit?: string;
}

/**
 * Two numeric inputs under one heading, such as width x height, inside a `DialogForm`. A group
 * instead of one `<label>` around both inputs, which would name neither.
 */
export const NumberPairField = <T extends FormikValues>({
  fields,
  formik,
  label,
  required,
  separator,
  unit = 'px',
}: NumberPairFieldProps<T>) => {
  const groupId = useUniqueId('number-pair');
  return (
    <div aria-labelledby={groupId} role="group">
      <span className={required ? 'label required' : 'label'} id={groupId}>
        {label}
      </span>
      {fields.map((field, index) => {
        const touched = Boolean(formik.touched[field.name]);
        const error = formik.errors[field.name];
        const message = touched && error ? String(error) : undefined;
        const helperId = `${groupId}-${field.name}-helper`;
        return (
          <Fragment key={field.name}>
            {index > 0 && separator}
            <FormControl sx={{ width: '13ch', ...field.sx }} variant="outlined">
              <OutlinedInput
                endAdornment={<InputAdornment position="end">{unit}</InputAdornment>}
                error={message != null}
                inputProps={{
                  'aria-label': field.label,
                  'aria-describedby': message ? helperId : undefined,
                }}
                name={field.name}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="number"
                value={formik.values[field.name]}
              />
              <FormHelperText error={message != null} id={helperId}>
                {message ?? field.label}
              </FormHelperText>
            </FormControl>
          </Fragment>
        );
      })}
    </div>
  );
};
